/**
 * Which build you are looking at.
 *
 * Worth showing because the app updates on its own terms: it asks for a new
 * version on focus and offers a reload, and an installed copy can sit in a
 * dock for a while in between. A visible build is how you settle "did that
 * actually update" without opening dev tools.
 *
 * The commit rather than a version number, because nothing bumps a version
 * number here and a stale one would be worse than none. The commit is the
 * thing you can actually look up.
 */

/**
 * Read defensively: these are substituted by the bundler, so they simply do
 * not exist anywhere Vite has not been — a test runner, a node script. That
 * makes the "unknown" path real rather than theoretical, which is the reason
 * it says so out loud instead of inventing a number.
 */
export const APP_COMMIT =
  typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : 'unknown'
export const APP_COMMIT_DATE =
  typeof __APP_COMMIT_DATE__ === 'string' ? __APP_COMMIT_DATE__ : ''

/** "0734c50 · 29 Jul". Short enough to sit quietly under the navigation. */
export function formatVersion(
  commit: string = APP_COMMIT,
  isoDate: string = APP_COMMIT_DATE,
): string {
  if (commit.length === 0 || commit === 'unknown') return 'unknown build'

  const date = shortDate(isoDate)
  return date === null ? commit : `${commit} · ${date}`
}

/** The full timestamp, for the tooltip. Empty when there is nothing to say. */
export function versionTitle(isoDate: string = APP_COMMIT_DATE): string {
  const parsed = parse(isoDate)
  return parsed === null ? '' : `Built from a commit made ${parsed.toString()}`
}

function shortDate(isoDate: string): string | null {
  const parsed = parse(isoDate)
  if (parsed === null) return null

  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  })
}

function parse(isoDate: string): Date | null {
  if (isoDate.length === 0) return null
  const parsed = new Date(isoDate)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}
